import React, { useState } from 'react';
import { ArrowRightIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { usePortal } from '../contexts/PortalContext';
import { brand } from '../data/projects';

/**
 * Studio-side access. Kept entirely separate from client access.
 *
 * Recovery is a code flow rather than a clickable link: Supabase emails a
 * recovery code, the staff member types it here, and only then can they set a
 * new password — so nothing can consume the link before they arrive.
 */
type Mode = 'signin' | 'forgot' | 'code' | 'newPassword' | 'done';
const FIELD = 'mt-4 w-full border-0 border-b border-paper/25 bg-transparent pb-3 font-display text-xl text-paper placeholder:text-paper/25 focus:border-paper focus:outline-none';
const FIELD_WITH_TOGGLE = `${FIELD} pr-10`;
const COPY: Record<Mode, {
  label: string;
  title: string;
}> = {
  signin: {
    label: 'Staff Access',
    title: 'Sign in to\nPortal.'
  },
  forgot: {
    label: 'Password Reset',
    title: 'Reset your\npassword.'
  },
  code: {
    label: 'Password Reset',
    title: 'Check your\nemail.'
  },
  newPassword: {
    label: 'Password Reset',
    title: 'Choose a new\npassword.'
  },
  done: {
    label: 'Password Reset',
    title: 'Password\nupdated.'
  }
};
export function StaffLogin() {
  const {
    staffSignIn,
    staffResetPassword,
    staffVerifyRecoveryCode,
    staffCompletePasswordReset,
    notify
  } = usePortal();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  function go(next: Mode) {
    setMode(next);
    setPassword('');
    setError('');
  }
  function backToSignIn() {
    setCode('');
    setNewPassword('');
    setConfirm('');
    go('signin');
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      if (mode === 'signin') {
        const result = await staffSignIn(email, password);
        if (!result.ok) setError(result.error ?? 'Email or password incorrect');
        return;
      }
      if (mode === 'forgot') {
        if (!email.trim()) {
          setError('Enter your Portal email address.');
          return;
        }
        await staffResetPassword(email);
        go('code');
        return;
      }
      if (mode === 'code') {
        const result = await staffVerifyRecoveryCode(email, code);
        if (!result.ok) {
          setError(result.error ?? 'That recovery code is no longer valid. Please request a new one.');
          return;
        }
        setCode('');
        go('newPassword');
        return;
      }
      if (mode === 'newPassword') {
        const result = await staffCompletePasswordReset(newPassword, confirm);
        if (!result.ok) {
          setError(result.error ?? 'Password could not be updated.');
          return;
        }
        setNewPassword('');
        setConfirm('');
        notify('Password updated');
        setMode('done');
      }
    } finally {
      setBusy(false);
    }
  }
  const submitLabel = mode === 'signin' ? 'Sign In to Portal' : mode === 'forgot' ? 'Send Recovery Code' : mode === 'code' ? 'Verify Code' : 'Update Password';
  return <div className="flex min-h-screen w-full items-center justify-center bg-ink px-6 py-20 text-paper" data-id="element-2635" data-mp="3no7gjvkuqww5">
      <div className="w-full max-w-md" data-id="element-2636" data-mp="5iueg3bfbdgj">
        <img src={brand.lockup} alt="Portal Productions" className="h-8 w-auto brightness-0 invert sm:h-10" data-id="element-2637" data-mp="k9oqxtssc6c" />

        <p className="label mt-16 text-paper/50" data-id="element-2638" data-mp="27bikekfgcf00">{COPY[mode].label}</p>

        <h1 className="mt-7 whitespace-pre-line font-display text-4xl leading-[0.95] tracking-tight sm:text-5xl" data-id="element-2639" data-mp="22c5yc1wenbyh">
          {COPY[mode].title}
        </h1>

        {mode === 'code' && <p className="label-sm mt-10 leading-relaxed text-paper/50" data-id="element-2640" data-mp="28rmorw23rr1h">
            Enter the recovery code we sent to {email.trim()}.
          </p>}

        {mode === 'done' ? <>
            <p className="label-sm mt-10 leading-relaxed text-paper/50" data-id="element-2641" data-mp="2iqly7guozqpj">
              You can now sign in with your new password.
            </p>
            <button type="button" onClick={backToSignIn} className="label group mt-14 flex w-full items-center justify-between gap-6 border border-paper px-8 py-5 text-paper transition-colors duration-150 ease-out hover:bg-paper hover:text-ink" data-id="element-2642" data-mp="1l3wm2e69zxpy">
              Back to Staff Access
              <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-2643" data-mp="12bu3gxa2q7d2" />
            </button>
          </> : <form onSubmit={submit} className="mt-14" data-id="element-2644" data-mp="zqcwirzu9d6g">
            {(mode === 'signin' || mode === 'forgot') && <label className="block" data-id="element-2645" data-mp="utmfa1casbjg">
                <span className="label block text-paper/40" data-id="element-2646" data-mp="pt88emmfn7n0">Email</span>
                <input type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="e.g. name@portalproductions.me" autoComplete="username" className={FIELD} data-id="element-2647" data-mp="prdnv9f2phjg" />
              </label>}

            {mode === 'signin' && <label className="mt-10 block" data-id="element-2648" data-mp="738n8atpqcov">
                <span className="label block text-paper/40" data-id="element-2649" data-mp="23w2bvmejfox">Password</span>
                <input type="password" value={password} onChange={event => setPassword(event.target.value)} autoComplete="current-password" className={FIELD} data-id="element-2650" data-mp="21jcyxebe31u" />
              </label>}

            {mode === 'code' && <label className="block" data-id="element-2651" data-mp="3jmun80otos7p">
                <span className="label block text-paper/40" data-id="element-2652" data-mp="3jnw62rc2yizz">Recovery code</span>
                {/* Numeric entry, but no fixed length — Supabase alone decides
                    whether the code it sent is valid. */}
                <input type="text" value={code} onChange={event => setCode(event.target.value.replace(/\D+/g, ''))} autoComplete="one-time-code" inputMode="numeric" className={FIELD} data-id="element-2653" data-mp="3anr44wpnoct7" />
              </label>}

            {mode === 'newPassword' && <>
                <label className="relative block" data-id="element-2654" data-mp="3ti62ck4581fz">
                  <span className="label block text-paper/40" data-id="element-2655" data-mp="l3xf7rke42v7">New password</span>
                  <input type={showNew ? 'text' : 'password'} value={newPassword} onChange={event => setNewPassword(event.target.value)} autoComplete="new-password" className={FIELD_WITH_TOGGLE} data-id="element-2656" data-mp="pvntg8mg7arw" />
                  <button type="button" onClick={() => setShowNew(current => !current)} aria-label={showNew ? 'Hide password' : 'Show password'} aria-pressed={showNew} className="absolute bottom-3 right-0 text-paper/40 transition-colors duration-150 ease-out hover:text-paper/80" data-id="element-2657" data-mp="18ypnkkyd9kqo">
                    {showNew ? <EyeOffIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-2658" data-mp="q7gg0h85p61q" /> : <EyeIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-2659" data-mp="lcsy517nr9w4" />}
                  </button>
                </label>

                <label className="relative mt-10 block" data-id="element-2660" data-mp="1wqejsceb6tcw">
                  <span className="label block text-paper/40" data-id="element-2661" data-mp="1wrzf448g6gbt">Confirm new password</span>
                  <input type={showConfirm ? 'text' : 'password'} value={confirm} onChange={event => setConfirm(event.target.value)} autoComplete="new-password" className={FIELD_WITH_TOGGLE} data-id="element-2662" data-mp="3r3quf7hjxxkc" />
                  <button type="button" onClick={() => setShowConfirm(current => !current)} aria-label={showConfirm ? 'Hide password' : 'Show password'} aria-pressed={showConfirm} className="absolute bottom-3 right-0 text-paper/40 transition-colors duration-150 ease-out hover:text-paper/80" data-id="element-2663" data-mp="8touqvk2y5hs">
                    {showConfirm ? <EyeOffIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-2664" data-mp="2oka2cnhf63ms" /> : <EyeIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-2665" data-mp="2tiuudzmiph05" />}
                  </button>
                </label>
              </>}

            {error && <p role="alert" className="label-sm mt-8 leading-relaxed text-paper" data-id="element-2666" data-mp="2h72718bewl5r">
                {error}
              </p>}

            <button type="submit" disabled={busy} className="label group mt-14 flex w-full items-center justify-between gap-6 border border-paper px-8 py-5 text-paper transition-colors duration-150 ease-out hover:bg-paper hover:text-ink disabled:border-paper/25 disabled:text-paper/40 disabled:hover:bg-transparent disabled:hover:text-paper/40" data-id="element-2667" data-mp="2c6ef8kfd00ah">
              {submitLabel}
              <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-2668" data-mp="2qy6ku4pcpzz0" />
            </button>
          </form>}

        {mode !== 'done' && <div className="mt-10 flex flex-wrap items-center gap-8" data-id="element-2669" data-mp="1fkumg9cd29k8">
            {mode === 'signin' ? <button type="button" onClick={() => go('forgot')} className="label-sm text-paper/35 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper/70" data-id="element-2670" data-mp="31s2s918s1yb2">
                Forgot password?
              </button> : <button type="button" onClick={backToSignIn} className="label-sm text-paper/35 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper/70" data-id="element-2671" data-mp="3pgvrb6kp4m9w">
                Back to sign in
              </button>}
          </div>}

        <p className="label-sm mt-16 max-w-sm leading-relaxed text-paper/25" data-id="element-2672" data-mp="3tk1u248lnvbm">
          Portal team accounts only, by invitation. Client access is provided separately by secure
          project link.
        </p>
      </div>
    </div>;
}